import { useState, useRef, useEffect } from "react";
import { useApi } from "../../hooks/use-api";
import {
  apiStudentStudys,
  API_EXAM_SUBJECTS,
  API_EXAM_CHAPTERS,
  API_EXAM_MCQ,
  apiChatHistoryExamFilter,
  API_AVATAR_EXAM_ASK,
  API_AVATAR_START,
  API_AVATAR_STOP,
  API_AVATAR_LOG,
  API_ASSEST_URL,
  API_AUDIO_CHAT,
  API_VIDEO_CHAT,
  API_VIDEO_MARK,
  API_LEARNING_CHALLENGE_UPDATE,
} from "../../utils/api-routes";
import {
  mapChapterToNewName,
  mapChapterToApiName,
} from "../../utils/chapter-mapping";
import { useSingleton } from "../../hooks/use-singleton";
import { useNotify } from "../../hooks/use-notify";

const useHandler = () => {
  const { get, post } = useApi();
  const { toggleNotify } = useNotify();
  const { mail, user, role, board, examType } = useSingleton();
  const [inputText, setInputText] = useState("");
  const [conversation, setConversation] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isEnable, setIsEnable] = useState(false);
  const [mode, setMode] = useState(false);
  const [avatarSession, setAvatarSession] = useState(null);
  const [lastAvatarSessionId, setLastAvatarSessionId] = useState(null);
  const [avatarToast, setAvatarToast] = useState(null);
  const [isLiveAvatarLoading, setIsLiveAvatarLoading] = useState(false);
  const [isHistoryEmpty, setIsHistoryEmpty] = useState(false);

  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedChapter, setSelectedChapter] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [assetData, setAssetData] = useState(null);
  const [popupMedia, setPopupMedia] = useState(null);

  const subjectRef = useRef(null);
  const chapterRef = useRef(null);
  const languageRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const audioRef = useRef(null);
  const videoRef = useRef(null);

  const [classdetails, setClassDetails] = useState({});
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [showAskBox, setShowAskBox] = useState(true);
  const [question, setQuestion] = useState("");
  const [audioRes, setAudioRes] = useState({});
  const [videoRes, setVideoRes] = useState({});

  const isStartEnabled =
    !!selectedSubject && selectedChapter.length > 0 && !!selectedLanguage;

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = async (text) => {
    const question = (typeof text === "string" ? text : inputText).trim();
    if (!question) return;

    // Add user message
    const userMessage = { sender: "user", text: question };
    setConversation((prev) => [...prev, userMessage]);

    // Clear input box
    setInputText("");

    setIsLoading(true);

    // 2️⃣ Add temporary loading bot message
    setConversation((prev) => [
      ...prev,
      { sender: "bot", text: "Loading...", isLoading: true },
    ]);

    try {
      const mappedChapter = selectedChapter
        .map((ch) => mapChapterToNewName(ch))
        .join(", ");
      const payload = {
        mail: mail,
        // board: board,
        exam: classdetails?.data?.class,
        subject: selectedSubject,
        chapter: mappedChapter,
        question: question, // Corrected variable reference
        language: selectedLanguage,
      };

      if (mode) {
        // --- LIVE AVATAR INTERACTION ---
        const response = await post(API_AVATAR_EXAM_ASK, payload, {
          headers: {
            "x-user-mail": mail || user?.data?.email || "",
          },
        });

        if (response.status !== 200 && response.status !== 201) {
          throw new Error(`Avatar Exam Ask failed: ${response.status}`);
        }

        // Drop the loading bubble, Avatar will respond audibly
        setConversation((prev) => prev.filter((msg) => !msg.isLoading));
      } else {
        // --- STANDARD TEXT CHATBOT ---
        const response = await post(API_EXAM_MCQ, payload);

        setConversation((prev) =>
          prev.map((msg) =>
            msg.isLoading
              ? {
                  sender: "bot",
                  text:
                    response?.data?.answer ||
                    "Sorry, I couldn't understand that.",
                  audioPath: response?.data?.audio_path || null,
                }
              : msg,
          ),
        );
      }
    } catch (error) {
      console.error("API Error:", error);
      setConversation((prev) =>
        prev.map((msg) =>
          msg.isLoading
            ? {
                sender: "bot",
                text: "Sorry, there was an error processing your request.",
              }
            : msg,
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        subjectRef.current?.contains(e.target) ||
        chapterRef.current?.contains(e.target) ||
        languageRef.current?.contains(e.target)
      )
        return;

      setOpenDropdown(null);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getUserStudiesDetails = async () => {
    try {
      const res = await get(apiStudentStudys(mail));
      if (res) {
        // console.log(res?.data)
        setClassDetails(res?.data);
        const payload = {
          board: res?.data?.data?.board,
          exam: res?.data?.data?.focus || res?.data?.data?.class,
        };
        getSubjectDetails(payload);
      } else {
        alert(res?.message);
      }
    } catch (e) {
      console.log("Error on getting user studiesdetails", e);
    }
  };

  const getSubjectDetails = async (payload) => {
    setIsLoading(true);
    try {
      const res = await post(API_EXAM_SUBJECTS, payload);
      if (res) {
        setSubjects(res?.data?.subjects);
        setIsLoading(false);
      } else {
        alert(res?.message);
      }
    } catch (e) {
      setIsLoading(false);
      console.log("Error on getting Subject Details", e);
    }
  };

  const getChapterDetails = async (payload) => {
    setIsLoading(true);
    try {
      const res = await post(API_EXAM_CHAPTERS, payload);
      if (res) {
        const rawChapters = res?.data?.chapters || [];
        const formattedChapters = rawChapters.map((ch) => mapChapterToNewName(ch));
        setChapters(formattedChapters);
        setIsLoading(false);
      }
    } catch (e) {
      setIsLoading(false);
      console.log("Error on getting chapeter details", e);
    }
  };

  useEffect(() => {
    getUserStudiesDetails();
  }, []);

  useEffect(() => {
    if (!selectedSubject || !classdetails?.data?.class) return;
    setSelectedChapter([]);
    const payload = {
      board: board,
      exam: classdetails?.data?.class,
      subject: selectedSubject,
    };
    getChapterDetails(payload);
  }, [selectedSubject]);

  useEffect(() => {
    if (!isEnable) {
      setConversation([]);
    }
  }, [selectedSubject, selectedChapter, selectedLanguage]);

  const onClickStart = async () => {
    setConversation([]);
    setIsEnable(true);
    setIsHistoryEmpty(false);
  };

  const onClickLearnVideo = async () => {
    setConversation([]);
    setIsHistoryEmpty(false);
    setShowPopup(true);

    try {
      const mappedChapter = selectedChapter
        .map((ch) => mapChapterToNewName(ch))
        .join(", ");
      const payload = {
        mail: mail,
        exam:
          classdetails?.data?.board ||
          user?.data?.focus ||
          classdetails?.data?.class,
        subject: selectedSubject,
        chapter: mappedChapter,
        language: selectedLanguage,
      };

      let res = await post(API_ASSEST_URL, payload);

      if (res && res?.data?.assets) {
        setAssetData(res?.data?.assets || []);
      }
    } catch (error) {
      console.error("API error:", error);
      // Fallback: try with legacy/old chapter name
      try {
        const legacyChapter = selectedChapter
          .map((ch) => mapChapterToApiName(ch))
          .join(", ");
        if (legacyChapter) {
          const fallbackPayload = {
            mail: mail,
            exam:
              classdetails?.data?.board ||
              user?.data?.focus ||
              classdetails?.data?.class,
            subject: selectedSubject,
            chapter: legacyChapter,
            language: selectedLanguage,
          };
          const fallbackRes = await post(API_ASSEST_URL, fallbackPayload);
          if (fallbackRes?.data?.assets) {
            setAssetData(fallbackRes?.data?.assets || []);
          }
        }
      } catch (fallbackError) {
        console.error("Fallback API error:", fallbackError);
      }
    }
  };

  const getHistoryDetails = async () => {
    try {
      const mappedChapter = selectedChapter
        .map((ch) => mapChapterToNewName(ch))
        .join(", ");
      let res = await get(
        apiChatHistoryExamFilter(mail, selectedSubject, mappedChapter),
      );
      if (
        (!res?.data?.history || res?.data?.history?.length === 0) &&
        selectedChapter.length > 0
      ) {
        const legacyChapter = selectedChapter
          .map((ch) => mapChapterToApiName(ch))
          .join(", ");
        if (legacyChapter !== mappedChapter) {
          try {
            const fallbackRes = await get(
              apiChatHistoryExamFilter(mail, selectedSubject, legacyChapter),
            );
            if (fallbackRes?.data?.history?.length) {
              res = fallbackRes;
            }
          } catch (e) {}
        }
      }

      if (res?.data?.history?.length) {
        const formattedConversation = [];

        res?.data?.history?.forEach((item) => {
          // user message
          formattedConversation.push({
            sender: "user",
            text: item.user_question,
            time: item.created_at,
          });

          // bot message
          formattedConversation.push({
            sender: "bot",
            text: item?.ai_answer,
            audioPath: item?.audio_path || null,
          });
        });

        setConversation(formattedConversation);
        setIsHistoryEmpty(false);
      } else {
        setConversation([]);
        setIsHistoryEmpty(true);
      }
    } catch (e) {
      console.log("ERROR IS COMES FROM GETHISTORY API", e);
    }
  };

  const onClickHistroy = async () => {
    if (isStartEnabled) {
      await getHistoryDetails();
      setIsEnable(true);
    }
  };

  const onToggleBtn = () => {
    if (role !== "guest") {
      setMode((p) => !p);
      setConversation([]);
      setIsEnable(false);
      setAvatarSession(null);
    } else {
      toggleNotify({
        type: "success",
        title: "Message",
        description: "Please Subscribe",
      });
    }
  };

  const onLiveAvatarApi = async () => {
    setIsEnable(true);
    setIsLiveAvatarLoading(true);

    setConversation([
      { sender: "bot", text: "Connecting to Live Avatar...", isLoading: true },
    ]);

    try {
      const response = await post(
        `${API_AVATAR_START}?language=${selectedLanguage}`,
        {},
        {
          headers: {
            "x-user-mail": mail || user?.data?.email || "",
          },
        },
      );

      console.log("AVATAR API STATUS:", response.status);
      const data = response.data;
      console.log("AVATAR START RESPONSE:", data);

      if (response.status === 200 || response.status === 201) {
        setAvatarSession(data || { connected: true });
        if (data?.session_id) setLastAvatarSessionId(data.session_id);
        setIsLiveAvatarLoading(false);
        setConversation([
          {
            sender: "bot",
            text: "Live Avatar connected successfully! You can now start interacting.",
          },
        ]);
      } else {
        throw new Error(data?.message || `Error: ${response.status}`);
      }
    } catch (err) {
      console.error("Error starting Avatar API:", err);
      setIsEnable(false);
      const msg = err?.message || "Unable to connect to Live Avatar.";
      setAvatarToast({
        type: "error",
        message:
          msg.includes("502") || msg.includes("403")
            ? "Unable to connect: Avatar service unavailable."
            : msg,
      });
      setTimeout(() => setAvatarToast(null), 4000);
      setConversation([]);
    } finally {
      setIsLiveAvatarLoading(false);
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  }, [conversation]);

  const onEndAvatarInteraction = async () => {
    setConversation([]);
    setIsEnable(false);
    setAvatarSession(null);
    try {
      const response = await post(
        API_AVATAR_STOP,
        {},
        {
          headers: {
            "x-user-mail": mail || user?.data?.email || "",
          },
        },
      );
      console.log("AVATAR STOP RESPONSE STATUS:", response.status);
    } catch (err) {
      console.error("Error stopping Avatar API:", err);
    }
  };

  const downloadAvatarLog = async () => {
    const sid = avatarSession?.session_id || lastAvatarSessionId;
    if (!sid) {
      alert("No active avatar session found to download logs.");
      return;
    }
    try {
      const response = await get(`${API_AVATAR_LOG}?session_id=${sid}`, {
        headers: {
          "x-user-mail": mail || user?.data?.email || "",
        },
      });
      const data = response?.data;
      if (response && response.status === 200) {
        if (response.data?.pdf_url) {
          window.open(response.data.pdf_url, "_blank");
        } else {
          alert("Unable to generate log PDF.");
        }
      }
    } catch (err) {
      console.error("Error downloading log:", err);
      alert("Error downloading log file.");
    }
  };

  const aiAudioChat = async (filters, audio_content) => {
    const payload = {
      Audio_content: audio_content,
      // filters: audio_content,
      Question: question,
      language: "English",
    };

    try {
      const res = await post(API_AUDIO_CHAT, payload);
      if (res) {
        setAudioRes(res?.data);
      }
    } catch (e) {
      console.log("ERROR COMES FROM AUDIO CHAT API", e);
    }
  };

  const aiVideoChat = async (video_content) => {
    const mappedChapter = selectedChapter
      .map((ch) => mapChapterToNewName(ch))
      .join(", ");
    const payload = {
      // Video_content: video_content,
      filters: {
        exam: classdetails?.data?.class,
        subject: selectedSubject,
        chapter: mappedChapter,
      },
      Question: question,
      language: "English",
    };

    try {
      const res = await post(API_VIDEO_CHAT, payload);
      if (res) {
        setVideoRes(res?.data);
      }
    } catch (e) {
      console.log("ERROR COMES FROM AI VIDEO CHAT API", e);
    }
  };

  const handleClearChat = () => {
    setConversation([]); // clear messages only
  };

  const handleNewChat = () => {
    // clear chat
    setConversation([]);

    // reset filters
    setSelectedSubject(null);
    setSelectedChapter([]);
    setSelectedLanguage(null);

    // reset input
    setInputText("");
    setOpenDropdown(null);

    // ✅ IMPORTANT
    setIsEnable(false); // 👈 this will hide input section

    // optional resets
    setIsHistoryEmpty(false);
    onEndAvatarInteraction?.();
  };

  const learningChallengeProgress = async () => {
    const payload = {
      email: mail,
      action_type: "video",
      watch_percentage: 100,
      questions_attempted: 0,
      study_minutes: 0,
    };
    try {
      const res = await post(API_LEARNING_CHALLENGE_UPDATE, payload);
      if (res) {
        console.log(res);
      }
    } catch (e) {
      console.log("ERROR COMES FROM LEARNING CHALLANGE API", e);
    }
  };

  const videoMarkCompleted = async (videoUrl) => {
    const mappedChapter = selectedChapter
      .map((ch) => mapChapterToNewName(ch))
      .join(", ");
    const payload = {
      user_email: mail,
      video_url: videoUrl,
      course_type: examType,
      filters: {
        exam: classdetails?.data?.class,
        subject: selectedSubject,
        chapter: mappedChapter,
      },
    };
    try {
      const res = await post(API_VIDEO_MARK, payload);
      if (res.data) {
        await learningChallengeProgress();
      }
    } catch (e) {
      console.log("ERROR COMES FROM VIDEO MARK COMPELETED", e);
    }
  };

  return {
    handleClearChat,
    handleNewChat,
    subjectRef,
    chapterRef,
    languageRef,
    conversation,
    selectedChapter,
    selectedLanguage,
    selectedSubject,
    openDropdown,
    inputText,
    classdetails,
    subjects,
    chapters,
    isLoading,
    user,
    isStartEnabled,
    isEnable,
    mode,
    avatarSession,
    avatarToast,
    messagesEndRef,
    role,
    onToggleBtn,
    onClickHistroy,
    onClickStart,
    setSelectedSubject,
    setSelectedChapter,
    setSelectedLanguage,
    setOpenDropdown,
    setInputText,
    handleSend,
    handleKeyPress,
    getHistoryDetails,
    onLiveAvatarApi,
    onEndAvatarInteraction,
    downloadAvatarLog,
    videoMarkCompleted,
    lastAvatarSessionId,
    isHistoryEmpty,
    isLiveAvatarLoading,
    showPopup,
    setShowPopup,
    popupMedia,
    setPopupMedia,
    assetData,
    onClickLearnVideo,
    showAskBox,
    setShowAskBox,
    audioRef,
    videoRef,
    question,
    setQuestion,
    inputRef,
    aiAudioChat,
    aiVideoChat,
    videoRes,
    audioRes,
  };
};

export default useHandler;
